import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { arrangeColumns } from '@user/components/xGrid'
import {
  selectPestPacLocationDuplicatesColumnOrder,
  useLoadListViewPreferencesOnce,
} from '@user/store/userPreferences'
import { GridDensity, useGridApiRef } from '@mui/x-data-grid-pro'
import { pestPacLocationsDuplicatesColumns } from '@user/components/Leads/AddLeadPage/FormComponents/DuplicateAlert/DuplicateDialog/PestPacLocations/tableColumn'
import { ILocationSearchModel } from '@user/query/useDuplicateLocationSearch'
import {
  selectCurrentUser,
  useCheckPermissions,
} from '@user/store/authorization'
import { guestRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import { externalProductPestPacName } from '@sales-center/common'
import { useNavigate } from 'react-router-dom'

interface Props {
  densityMode?: GridDensity
  highlights?: Record<string, string>
  createOppCallback: (location: ILocationSearchModel) => void
  handleDuplicateManageClick?: () => void
}

export const useGetColumnData = (props: Props) => {
  const {
    densityMode,
    highlights,
    createOppCallback,
    handleDuplicateManageClick,
  } = props
  const userPrefsLoading = useLoadListViewPreferencesOnce()
  const apiRef = useGridApiRef()
  const { externalProduct, clientUrl } = useSelector(selectCurrentUser)
  const navigate = useNavigate()

  const currentUser = useSelector(selectCurrentUser)
  const isGuestUser = currentUser?.roleName === guestRoleName

  const [canCreateOpps] = useCheckPermissions(
    'opportunities_convert_/_save_for_later',
  )

  const [canAddLeads] = useCheckPermissions('leads_add')

  const getExternalLocationUrl = useCallback(
    (locationId: string) => {
      if (!locationId) return ''

      switch (externalProduct) {
        case externalProductPestPacName:
          return `${clientUrl}/location/detail.asp?LocationID=${locationId}`
        default:
          return ''
      }
    },
    [externalProduct, clientUrl],
  )

  const onClickManage = useCallback(
    (action: string, location: ILocationSearchModel) => {
      const id = location.id.split('-')[0]
      switch (action) {
        case 'lead':
          if (handleDuplicateManageClick) handleDuplicateManageClick()
          return navigate(`/leads/addLead?locationId=${id}`)
        case 'opportunity':
          return createOppCallback({ ...location, id })
        case 'view':
          return window.open(
            getExternalLocationUrl ? getExternalLocationUrl(id) : '',
            '_blank',
          )
        default:
          return ''
      }
    },
    [createOppCallback, getExternalLocationUrl, navigate],
  )

  const columnOrder = useSelector(selectPestPacLocationDuplicatesColumnOrder)
  const [columns, columnVisibility] = useMemo(
    () =>
      arrangeColumns(
        columnOrder,
        pestPacLocationsDuplicatesColumns({
          onClickManage,
          densityMode,
          highlights,
          isGuestUser,
          canAddLeads,
          canCreateOpps,
        }),
        [],
        [],
      ),
    [
      columnOrder,
      onClickManage,
      densityMode,
      highlights,
      isGuestUser,
      canAddLeads,
      canCreateOpps,
    ],
  )

  const loading = userPrefsLoading

  return { loading, columns, columnVisibility, apiRef }
}
